import _ from 'lodash';
import { useState } from 'react';

import chapterImg1 from '../../../../assets/images/courses/collaboration-fundamentals/chapter6.1.1.jpg';
import chapterImg2 from '../../../../assets/images/courses/collaboration-fundamentals/chapter6.1.2.jpg';
import chapterImg3 from '../../../../assets/images/courses/collaboration-fundamentals/chapter6.1.3.jpg';
import chapterImg4 from '../../../../assets/images/courses/collaboration-fundamentals/chapter6.1.4.jpg';
import chapterImg5 from '../../../../assets/images/courses/collaboration-fundamentals/chapter6.2.jpg';
import chapterImg6 from '../../../../assets/images/courses/collaboration-fundamentals/chapter6.3.jpg';
import chapterImg7 from '../../../../assets/images/courses/collaboration-fundamentals/chapter6.4.png';
import chapterImg9 from '../../../../assets/images/courses/collaboration-fundamentals/chapter6.5.1.jpg';
import chapterImg10 from '../../../../assets/images/courses/collaboration-fundamentals/chapter6.5.2.jpg';
import chapterImg11 from '../../../../assets/images/courses/collaboration-fundamentals/chapter6.5.3.jpg';
import chapterImg12 from '../../../../assets/images/courses/collaboration-fundamentals/chapter6.5.4.jpg';
import chapterImg13 from '../../../../assets/images/courses/collaboration-fundamentals/chapter6.5.5.jpg';
import chapterImg8 from '../../../../assets/images/courses/collaboration-fundamentals/chapter6.5.jpg';
import introImg from '../../../../assets/images/courses/collaboration-fundamentals/chapter6.jpg';
import blue from '../../../../assets/images/courses/perception-understanding/chapter5/blue.png';
import color_energies from '../../../../assets/images/courses/perception-understanding/chapter5/color_energies.jpg';
import green from '../../../../assets/images/courses/perception-understanding/chapter5/green.png';
import red from '../../../../assets/images/courses/perception-understanding/chapter5/red.png';
import yellow from '../../../../assets/images/courses/perception-understanding/chapter5/yellow.png';
import { CourseChapter } from '../../../../courses/courses.types';
import { CustomAccordion } from '../../../common/Accordion';
import { CarouselComponent } from '../../CarouselComponent';
import { CustomAudioTabsBlock, CustomTabPanel } from '../../CustomAudioTabs';
import { MediaBlock } from '../../MediaBlock';
import { TabsBlock } from '../../TabsBlock';
import { ToggleCard } from '../../ToggleCard';

interface CollaborationFundamentalsProps {
  chapter: CourseChapter;
  onNextChapter: (nextIndex: number) => void;
  totalPages: number;
  courseId?: string;
  onQuizResult?: (result: boolean | undefined) => void;
}

export const CollaborationFundamentalsChapter6: React.FC<CollaborationFundamentalsProps> = ({
  chapter,
  onNextChapter,
  totalPages,
  // courseId,
}) => {
  const [energyTabValue, setEnergyTabValue] = useState<number | null>(null);
  const [openCardPosition, setOpenCardPosition] = useState<string | null>(null);

  const handleNextChapterClick = () => {
    onNextChapter(chapter.index + 1);
  };

  const handleToggleCard = (position: string) => {
    setOpenCardPosition((prevPosition) => (prevPosition === position ? null : position));
  };

  const tabsData = [
    {
      title: 'Red',
      textColor: '',
      bgColor: 'bg-fiery-red',
    },
    {
      title: 'Yellow',
      textColor: '',
      bgColor: 'bg-sunshine-yellow',
    },
    {
      title: 'Green',
      textColor: '',
      bgColor: 'bg-earth-green',
    },
    {
      title: 'Blue',
      textColor: '',
      bgColor: 'bg-cool-blue',
    },
  ];
  const TabsData = [
    {
      title: 'Missing Fiery Red',
      htmlContent: `<p>Now, imagine the same team but without Amy’s Sunshine Yellow energy. Ben is still supportive and ensures everyone gets along (Earth Green), Chris is still detail-oriented and organized (Cool Blue), and this time you have David, who is great at making quick decisions and leading the team (Fiery Red). <br/> <br/>Without Amy's creativity and enthusiasm, your team finds it hard to come up with innovative ideas. The team meetings feel a bit dull, and everyone is just going through the motions. David is excellent at pushing the team forward and making decisions, but the lack of creative spark makes the project less exciting and original. As a result, your project might be well-organized and completed on time, but it might not stand out or capture the audience’s interest as much as it could have with Amy’s input.</p>`,
    },
    {
      title: 'Missing Sunshine Yellow',
      htmlContent: `<p>In explaining your reasoning and thinking, key phrases to use are “So, I’m hearing that you like this part, but not that aspect. Would you agree?”, “It sounds to me like…” and “I’m thinking that x makes sense, but do others agree?” &nbsp;</p>`,
    },
  ];
  const slidesData = [
    {
      heading: 'Introduction',
      paragraph: `<p>By appreciating these differences, you can see what everyone brings to the table, making your team stronger. By leveraging these insights, you can enhance your team's performance and foster a positive, collaborative atmosphere.</p>`,
      list: [],
      image: chapterImg9,
    },
    {
      heading: 'Understanding Differences:',
      paragraph: `<p> Knowing that everyone has their own unique strengths and preferences can help you appreciate what each person brings to the table. It's like having a diverse team of superheroes, each with their own special abilities.</p>`,
      list: [],
      image: chapterImg10,
    },
    {
      heading: 'Better Communication:',
      paragraph: `<p>By understanding the different communication styles associated with each color energy, you can learn how to communicate more effectively with your teammates. Whether it's being more direct with Fiery Reds or bringing enthusiasm to your interactions with Sunshine Yellows, tailoring your communication can make a big difference.</p>`,
      list: [],
      image: chapterImg11,
    },
    {
      heading: 'Building Trust:',
      paragraph: `<p>Trust is super important when it comes to working together. By respecting each other's differences and finding common ground, you can build trust and create a supportive team environment where everyone feels valued.</p>`,
      list: [],
      image: chapterImg12,
    },
    {
      heading: 'Balancing Perspectives:',
      paragraph: `<p>Each color energy brings something unique to the table. By combining the strengths of different personalities, you can tackle challenges from multiple angles and come up with more creative solutions.</p>`,
      list: [],
    },
    {
      heading: 'Resolving Conflicts:',
      paragraph: `<p>Conflict happens, but knowing how to handle it constructively is key. By understanding where people are coming from and finding ways to work through disagreements, you can turn conflicts into opportunities for growth and learning.</p>`,
      list: [],
      image: chapterImg13,
    },
  ];

  const accordionData1 = [
    {
      id: 'panel1',
      title: 'Fiery Red: ',
      image: chapterImg1,
      content: `<p>These are the go-getters, the ones who like to take charge and get things done. They're all about efficiency and making things happen.</p> <br/> <br/> <Strong>Example</Strong>:<p>Meet Alex, the captain of the school soccer team. Alex is the kind of person who always takes charge, whether it's on the field or during a group project. Known for their fiery red energy, Alex is all about getting things done quickly and efficiently. When there's a task to be completed, Alex doesn't waste any time. They organize the team, set clear goals, and motivate everyone to give their best effort. Alex’s go-getter attitude ensures that the team is always prepared, focused, and ready to win. </p>`,
    },
    {
      id: 'panel2',
      title: 'Sunshine Yellow:',
      image: chapterImg2,
      content: `<p>Think of Sunshine Yellows as the social butterflies. They're outgoing, creative, and always up for trying new things. They bring a lot of energy to the team.</p> <br/> <br/> <Strong>Example</Strong>:<p>Meet Jamie, the life of every party and the most outgoing member of the drama club. Jamie's sunshine yellow energy lights up every room they enter. Always full of creative ideas and ready to try something new, Jamie brings a vibrant, positive energy to any team they're part of. Whether it's brainstorming unique themes for the school play or organizing fun social events, Jamie's enthusiasm and friendliness make everyone feel included and excited to participate. Their infectious energy and creativity inspire the whole team to think outside the box and enjoy the process.</p>`,
    },
    {
      id: 'panel3',
      title: 'Earth Green:',
      image: chapterImg3,
      content: `<p>Earth Greens are the peacekeepers. They're empathetic, supportive, and great at bringing people together. They care a lot about harmony and making sure everyone feels heard.</p> <br/> <br/> <Strong>Example</Strong>:<p>Meet Sam, the go-to person for advice and support in the student council. Sam has a calming earth green energy that makes everyone feel comfortable and valued. Known for their empathy and ability to listen, Sam is great at bringing people together and resolving conflicts. When there’s a disagreement, Sam steps in as the peacekeeper, ensuring everyone’s voice is heard and finding solutions that work for everyone. Their supportive nature and focus on harmony help create a positive, inclusive environment where everyone feels respected and understood.</p>`,
    },
    {
      id: 'panel4',
      title: 'Cool Blue:',
      image: chapterImg4,
      content: `<p>Cool Blues are the analytical thinkers. They love to solve problems, pay attention to details, and make sure everything is just right.</p> <br/> <br/> <Strong>Example</Strong>:<p>Meet Taylor, the top student in the math club. Taylor's cool blue energy shines through their love for solving complex problems and their meticulous attention to detail. Whenever there's a tricky equation or a challenging project, Taylor dives in with a calm and analytical approach, carefully working through each step to ensure accuracy. Known for their thoroughness and precision, Taylor makes sure everything is just right before presenting their work. Their logical thinking and methodical nature make Taylor an invaluable member of any team that values clear, well-thought-out solutions. </p>`,
    },
  ];

  return (
    <>
      <div className="content-wrap lg:border-l lg:pl-8 lg:pt-8 pt-4 pb-10">
        <p className="text-sm italic mb-7">
          Part {chapter.index} of {totalPages}
        </p>
        <h2 className="font-semibold text-3xl ">Insights Discovery Color Energies</h2>

        <MediaBlock mediaImage={introImg}></MediaBlock>
        <p className="text-2xl text-center font-light mt-10 mb-5">
          In today's world, teamwork is not just important, it's essential. Whether you're working
          on a school project, participating in extracurricular activities, or even just hanging out
          with friends, knowing how to collaborate effectively can make a huge difference.
        </p>

        <p className="text-xl text-left font-bold mt-10 mb-5">
          But here's the thing: everyone is different, and understanding those differences can help
          you work together even better. That's where Insights Discovery Color Energies come in.
          Let's break it down in a way that makes sense for you!
        </p>
        <h3 className="text-2xl text-purple-600 font-bold mt-4 mb-1">
          What Are Insights Discovery Color Energies?
        </h3>
        <p className="text-2xl text-center font-light mt-10 mb-5">
          Imagine if everyone had their own superpower – that's kind of what Insights Discovery
          Color Energies are like. They're all about understanding different personality types and
          how they affect the way people communicate and work together. Here's a quick overview from
          the perspective of a team:
        </p>

        <p className="text-l  font-light mb-8">Click the '+' icons to learn more.</p>
        {accordionData1.map((data) => (
          <CustomAccordion
            key={data.title}
            title={data.title}
            content={data.content}
            id={data.id}
            image={data?.image}
          />
        ))}
        <h2 className="font-bold text-2xl mt-20 mb-7 ">
          To learn more about individual color preferences, what their motto is, and which terms
          speak to them, click on the tabs below for each color energy.
        </h2>

        <div className="mb-10 mt-10">
          <CustomAudioTabsBlock
            tabsData={tabsData}
            onTabChange={(value) => setEnergyTabValue(value)}
          >
            {!_.isNil(energyTabValue) && (
              <>
                <CustomTabPanel index={0} value={energyTabValue}>
                  <div className="flex">
                    <div className="w-1/2 border-r border-dashed border-black pr-10">
                      <h3 className="text-lg border-b-2 border-red-500 mb-5 pb-2">
                        Individuals with a preference for Fiery Red energy
                      </h3>
                      <ul className="list-disc pl-5 mb-10">
                        <li className="mb-2 pl-4">
                          Are active and move ina positive and firm direction
                        </li>
                        <li className="mb-2 pl-4">Seek an outcome that is specific and tangible</li>
                        <li className="mb-2 pl-4">
                          Are single-minded and determined in their focus on results
                        </li>
                        <li className="mb-2 pl-4">
                          Have a strong determination that influences those they interact with
                        </li>
                        <li className="mb-2 pl-4">
                          Approach others in a direct and straightforward manner
                        </li>
                      </ul>
                      <p className="border border-black p-5 text-lg">
                        <span className="font-bold">MOTTO:</span> BE BRIEF, BE BRIGHT, BE GONE
                      </p>
                    </div>
                    <div className="w-1/2 pl-10">
                      <h3 className="text-lg border-b-2 border-red-500 mb-5 pb-2">
                        Fiery Red Style
                      </h3>
                      <p className="mb-7">Which Terms Speak to You?</p>
                      <div className="mb-5">
                        <p className="bg-red-500 text-white mb-1 inline-block">
                          GOOD DAY DESCRIPTORS:
                        </p>
                        <p>Competitive, Demanding, Determined, Strong-willed, Purposeful</p>
                      </div>
                      <div className="mb-5">
                        <p className="bg-red-500 text-white mb-1 inline-block">APPROACH:</p>
                        <p>Fast Paced and Decisive, Direct, Assertive</p>
                      </div>
                      <div className="mb-5">
                        <p className="bg-red-500 text-white mb-1 inline-block">GENERAL:</p>
                        <p>Busy, Formal, Efficient, Structured, Bold</p>
                      </div>
                      <div className="mb-5">
                        <p className="bg-red-500 text-white mb-1 inline-block">LIKES:</p>
                        <p>Productivity, Objectives, Control, Fast</p>
                      </div>
                      <div className="flex justify-end">
                        <div className="w-32 shadow-md bh-white">
                          <img src={red} alt="red" />
                        </div>
                      </div>
                    </div>
                  </div>
                </CustomTabPanel>
                <CustomTabPanel index={1} value={energyTabValue}>
                  <div className="flex">
                    <div className="w-1/2 border-r border-dashed border-black pr-10">
                      <h3 className="text-lg border-b-2 border-yellow-500 mb-5 pb-2">
                        Individuals with a preference for Sunshine Yellow energy
                      </h3>
                      <ul className="list-disc pl-5 mb-10">
                        <li className="mb-2 pl-4">Have a desire to be involved</li>
                        <li className="mb-2 pl-4">
                          Radiate enthusiasm and encourage participation
                        </li>
                        <li className="mb-2 pl-4">Enjoy and seek the company of others</li>
                        <li className="mb-2 pl-4">
                          Approach others in a persuasive, engaging and inviting manner
                        </li>
                        <li className="mb-2 pl-4">
                          Like to be noticed and appreciated for their contributions
                        </li>
                      </ul>
                      <p className="border border-black p-5 text-lg">
                        <span className="font-bold">MOTTO:</span> INVOLVE ME
                      </p>
                    </div>
                    <div className="w-1/2 pl-10">
                      <h3 className="text-lg border-b-2 border-yellow-500 mb-5 pb-2">
                        Sunshine Yellow Style
                      </h3>
                      <p className="mb-7">Which Terms Speak to You?</p>
                      <div className="mb-5">
                        <p className="bg-yellow-500 text-white mb-1 inline-block">
                          GOOD DAY DESCRIPTORS:
                        </p>
                        <p>Sociable, Dynamic, Demonstrative, Enthusiastic, Persuasive</p>
                      </div>
                      <div className="mb-5">
                        <p className="bg-yellow-500 text-white mb-1 inline-block">APPROACH:</p>
                        <p>Fast-paced, Spontaneous, Uplifting, Spirited</p>
                      </div>
                      <div className="mb-5">
                        <p className="bg-yellow-500 text-white mb-1 inline-block">GENERAL:</p>
                        <p>Personal, Friendly, Outgoing, Creative</p>
                      </div>
                      <div className="mb-5">
                        <p className="bg-yellow-500 text-white mb-1 inline-block">LIKES:</p>
                        <p>Variety, Relationships, People, Flexibility</p>
                      </div>
                      <div className="flex justify-end">
                        <div className="w-32 shadow-md bh-white">
                          <img src={yellow} alt="red" />
                        </div>
                      </div>
                    </div>
                  </div>
                </CustomTabPanel>
                <CustomTabPanel index={2} value={energyTabValue}>
                  <div className="flex">
                    <div className="w-1/2 border-r border-dashed border-black pr-10">
                      <h3 className="text-lg border-b-2 border-green-500 mb-5 pb-2">
                        Individuals with a preference for Earth Green energy
                      </h3>
                      <ul className="list-disc pl-5 mb-10">
                        <li className="mb-2 pl-4">Seek harmony and depth in relationships</li>
                        <li className="mb-2 pl-4">
                          View the world through what they value and what is important to them
                        </li>
                        <li className="mb-2 pl-4">
                          Ensure all individual perspectives are heard and considered in making
                          choices or decisions
                        </li>
                        <li className="mb-2 pl-4">
                          Prefer democratic approaches that respect the individual
                        </li>
                        <li className="mb-2 pl-4">
                          Defend what they value with quiet determination and persistence
                        </li>
                      </ul>
                      <p className="border border-black p-5 text-lg">
                        <span className="font-bold">MOTTO:</span> SHOW ME YOU CARE
                      </p>
                    </div>
                    <div className="w-1/2 pl-10">
                      <h3 className="text-lg border-b-2 border-green-500 mb-5 pb-2">
                        Earth Green Style
                      </h3>
                      <p className="mb-7">Which Terms Speak to You?</p>
                      <div className="mb-5">
                        <p className="bg-green-500 text-white mb-1 inline-block">
                          GOOD DAY DESCRIPTORS:
                        </p>
                        <p>Caring, Encouraging, Sharing, Patient</p>
                      </div>
                      <div className="mb-5">
                        <p className="bg-green-500 text-white mb-1 inline-block">APPROACH:</p>
                        <p>Slow-paced, Open, Casual, Conforming</p>
                      </div>
                      <div className="mb-5">
                        <p className="bg-green-500 text-white mb-1 inline-block">GENERAL:</p>
                        <p>Informal, Friendly, Calming, Personal</p>
                      </div>
                      <div className="mb-5">
                        <p className="bg-green-500 text-white mb-1 inline-block">LIKES:</p>
                        <p>Harmony, Close Relationships, Loyalty</p>
                      </div>
                      <div className="flex justify-end">
                        <div className="w-32 shadow-md bh-white">
                          <img src={green} alt="red" />
                        </div>
                      </div>
                    </div>
                  </div>
                </CustomTabPanel>
                <CustomTabPanel index={3} value={energyTabValue}>
                  <div className="flex">
                    <div className="w-1/2 border-r border-dashed border-black pr-10">
                      <h3 className="text-lg border-b-2 border-blue-500 mb-5 pb-2">
                        Individuals with a preference for Cool Blue energy
                      </h3>
                      <ul className="list-disc pl-5 mb-10">
                        <li className="mb-2 pl-4">Maintain a detached and objective standpoint</li>
                        <li className="mb-2 pl-4">Value independence and intellect</li>
                        <li className="mb-2 pl-4">
                          Think things through before committing to action
                        </li>
                        <li className="mb-2 pl-4">
                          Like information to be accurate and complete before proceeding
                        </li>
                        <li className="mb-2 pl-4">
                          Desire to know and understand the world around them
                        </li>
                      </ul>
                      <p className="border border-black p-5 text-lg">
                        <span className="font-bold">MOTTO:</span> GIVE ME THE DETAILS
                      </p>
                    </div>
                    <div className="w-1/2 pl-10">
                      <h3 className="text-lg border-b-2 border-blue-500 mb-5 pb-2">
                        Cool Blue Style
                      </h3>
                      <p className="mb-7">Which Terms Speak to You?</p>
                      <div className="mb-5">
                        <p className="bg-blue-500 text-white mb-1 inline-block">
                          GOOD DAY DESCRIPTORS:
                        </p>
                        <p>Cautious, Precise, Deliberate, Questioning, Formal</p>
                      </div>
                      <div className="mb-5">
                        <p className="bg-blue-500 text-white mb-1 inline-block">APPROACH:</p>
                        <p>Slower Paced, Systematic, Conservative</p>
                      </div>
                      <div className="mb-5">
                        <p className="bg-blue-500 text-white mb-1 inline-block">GENERAL:</p>
                        <p>Structured, Organized, Functional, Precise</p>
                      </div>
                      <div className="mb-5">
                        <p className="bg-blue-500 text-white mb-1 inline-block">LIKES:</p>
                        <p>Accuracy, Correctness, Thoroughness, Analysis</p>
                      </div>
                      <div className="flex justify-end">
                        <div className="w-32 shadow-md bh-white">
                          <img src={blue} alt="red" />
                        </div>
                      </div>
                    </div>
                  </div>
                </CustomTabPanel>
              </>
            )}
          </CustomAudioTabsBlock>
          {_.isNil(energyTabValue) && (
            <MediaBlock mediaImage={color_energies} cssClass="!mt-3"></MediaBlock>
          )}
        </div>

        <h3 className="text-2xl text-purple-600 font-bold mt-4 mb-1">Unique Gifts & Strengths</h3>
        <div className="md:flex items-center mb-7">
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg5} alt="" />
            <p className="flex-1 mt-5 text-center">8 Types</p>
          </div>
          <p className="flex-1 ">
            The 4 basic color energies of blue, red, green and yellow can then be broken down into 8
            types.
          </p>
          <br />
          <br />
        </div>
        <p className="text-2xl text-center font-light mt-10 mb-10">
          For a team to be successful, all color energies need to be present.
        </p>
        <div className="md:flex items-center mb-7">
          <p className="flex-1 ">
            Each team member brings their unique gifts and strengths to your team.
          </p>
          <br />
          <br />
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg6} alt="" />
            <p className="flex-1 mt-5 text-center">Unique strengths & Gifts</p>
          </div>
        </div>
        <p className="text-2xl text-center font-light mt-10 mb-10">
          When considering your approach when working with a team, it is important to look at the
          different color energy strengths of your team members as you are making decisions,
          innovating, solving problems, etc.
        </p>
        <h3 className="text-2xl text-purple-600 font-bold mt-4 mb-1">
          Appreciating All Types in Team Dynamics{' '}
        </h3>
        <p className="text-xl text-left font-light mt-5 mb-5">
          In the context of team dynamics, Insights Discovery helps team members appreciate the
          diverse strengths and perspectives each person brings to the group. For example, a team
          with a dominant Fiery Red leader might benefit from the enthusiasm and creativity of a
          Sunshine Yellow member, the patience and support of an Earth Green member, and the
          meticulousness and planning of a Cool Blue member.
        </p>
        <p className="text-2xl text-center font-light mt-10 mb-10">
          Recognizing these complementary skills enables teams to allocate tasks more effectively,
          resolve conflicts with greater understanding, and create a more inclusive and productive
          work environment. Ultimately, Insights Discovery promotes self-awareness, mutual respect,
          and adaptive communication strategies.
        </p>

        <p className="text-l text-left font-bold mt-5 mb-5">
          Click the markers below to learn each types:
        </p>
        <ol className="list-disc pl-5 mt-14 mb-14 text-xl font-light">
          <li className="pl-9 mb-7">Approach to Task</li>
          <li className="pl-9 mb-7">Keys to Success</li>
          <li className="pl-9 mb-7">Key Skills/Attributes</li>
        </ol>
        <div className="mb-20 mt-10">
          <div className="w-[250px] sm:w-[500px] md:w-[635px] mx-auto relative">
            <img className="w-full" src={chapterImg5} alt="" />
            <div className="absolute top-[0%] sm:top-[10%] left-0">
              <ToggleCard
                position="top-left"
                isOpen={openCardPosition === 'top-left'}
                onToggle={handleToggleCard}
                cardContent={
                  <div>
                    <p className="text-md sm:text-2xl font-bold mb-2 sm:mb-8">
                      Observer Team Dynamics
                    </p>
                    <ul>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Approach to Task</strong> - 'Doing it correctly!'
                        </p>
                      </li>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Keys to Success</strong> - they never stop learning
                        </p>
                      </li>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Key Skills or Attributes</strong> - sets standards, product
                          knowledge, analysis
                        </p>
                      </li>
                    </ul>
                  </div>
                }
              />
            </div>
            <div className="absolute top-[-18%] sm:top-[-12%] left-[45%]">
              <ToggleCard
                position="top"
                isOpen={openCardPosition === 'top'}
                onToggle={handleToggleCard}
                cardContent={
                  <div>
                    <p className="text-md sm:text-2xl font-bold mb-2 sm:mb-8">
                      Reformer Team Dynamics
                    </p>
                    <ul>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Approach to Task</strong> - 'Getting it done!'
                        </p>
                      </li>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Keys to Success</strong> - their determination
                        </p>
                      </li>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Key Skills or Attributes</strong> - determination, monitoring,
                          performance, discipline
                        </p>
                      </li>
                    </ul>
                  </div>
                }
              />
            </div>
            <div className="absolute top-[-2%] sm:top-[5%] right-[3%] sm:right-[5%]">
              <ToggleCard
                position="top-right"
                isOpen={openCardPosition === 'top-right'}
                onToggle={handleToggleCard}
                cardContent={
                  <div>
                    <p className="text-md sm:text-2xl font-bold mb-2 sm:mb-8">
                      Director Team Dynamics
                    </p>
                    <ul>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Approach to Task</strong> - 'Doing it now!'
                        </p>
                      </li>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Keys to Success</strong> - making a decision
                        </p>
                      </li>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Key Skills or Attributes</strong> - results, focus, decisive,
                          assertive
                        </p>
                      </li>
                    </ul>
                  </div>
                }
              />
            </div>
            <div className="absolute top-[45%] right-[-13%] sm:right-[-10%]">
              <ToggleCard
                position="right"
                isOpen={openCardPosition === 'right'}
                onToggle={handleToggleCard}
                cardContent={
                  <div>
                    <p className="text-md sm:text-2xl font-bold mb-2 sm:mb-8">
                      Motivator Team Dynamics
                    </p>
                    <ul>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Approach to Task</strong> - 'Getting it started!'
                        </p>
                      </li>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Keys to Success</strong> - believing in themselves
                        </p>
                      </li>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Key Skills or Attributes</strong> - drive, enthusiasm, positive
                          thinking
                        </p>
                      </li>
                    </ul>
                  </div>
                }
              />
            </div>
            <div className="absolute bottom-[2%] sm:bottom-[7%] right-[3%]">
              <ToggleCard
                position="bottom-right"
                isOpen={openCardPosition === 'bottom-right'}
                onToggle={handleToggleCard}
                cardContent={
                  <div>
                    <p className="text-md sm:text-2xl font-bold mb-2 sm:mb-8">
                      Inspirer Team Dynamics
                    </p>
                    <ul>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Approach to Task</strong> - 'Doing it together!'
                        </p>
                      </li>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Keys to Success</strong> - using their creativity
                        </p>
                      </li>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Key Skills or Attributes</strong> - persuasive, creative, people
                          skills
                        </p>
                      </li>
                    </ul>
                  </div>
                }
              />
            </div>
            <div className="absolute bottom-[-15%] sm:bottom-[-7%] left-[47%]">
              <ToggleCard
                position="bottom"
                isOpen={openCardPosition === 'bottom'}
                onToggle={handleToggleCard}
                cardContent={
                  <div>
                    <p className="text-md sm:text-2xl font-bold mb-2 sm:mb-8">
                      Helper Team Dynamics
                    </p>
                    <ul>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Approach to Task</strong> - 'Getting agreement!'
                        </p>
                      </li>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Keys to Success</strong> - balancing life and work
                        </p>
                      </li>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Key Skills or Attributes</strong> - flexible, helps others, shares
                          ideas
                        </p>
                      </li>
                    </ul>
                  </div>
                }
              />
            </div>
            <div className="absolute bottom-[2%] sm:bottom-[7%] left-[0%] sm:left-[3%]">
              <ToggleCard
                position="bottom-left"
                isOpen={openCardPosition === 'bottom-left'}
                onToggle={handleToggleCard}
                cardContent={
                  <div>
                    <p className="text-md sm:text-2xl font-bold mb-2 sm:mb-8">
                      {' '}
                      'Doing it discreetly!'
                    </p>
                    <ul>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Approach to Task</strong> - build relationships
                        </p>
                      </li>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Keys to Success</strong> - listens, loyal, team approach
                        </p>
                      </li>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Key Skills or Attributes</strong> - sets standards, product
                          knowledge, analysis
                        </p>
                      </li>
                    </ul>
                  </div>
                }
              />
            </div>
            <div className="absolute top-[40%] sm:top-[47%] left-[-15%] sm:left-[-10%]">
              <ToggleCard
                position="top-left"
                isOpen={openCardPosition === 'left'}
                onToggle={handleToggleCard}
                cardContent={
                  <div>
                    <p className="text-md sm:text-2xl font-bold mb-0 sm:mb-8">
                      Coordinator Team Dynamics
                    </p>
                    <ul>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Approach to Task</strong> - 'Doing it carefully!'
                        </p>
                      </li>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Keys to Success</strong> - managing their time
                        </p>
                      </li>
                      <li className="mb-2 sm:mb-5">
                        <p>
                          <strong>Key Skills or Attributes</strong> - planning, organization, time
                          management
                        </p>
                      </li>
                    </ul>
                  </div>
                }
              />
            </div>
          </div>
        </div>

        <h3 className="text-2xl text-purple-600 font-bold mt-20 mb-8">
          What Happens When a Color Energy is Missing?
        </h3>
        <p className="text-2xl text-center font-light mt-5 mb-10">
          In the realm of Insights Discovery, the balance of the four color energies—Fiery Red,
          Sunshine Yellow, Earth Green, and Cool Blue—is essential for creating a well-rounded and
          effective team. Each color energy brings unique strengths and perspectives that contribute
          to the overall functioning and success of the team.
        </p>

        <p className="text-xl text-left font-bold mt-10 mb-10">
          However, when one of these color energies is missing, the team may face significant
          challenges that hinder its performance and cohesion. Understanding the potential impact of
          an absent color energy can help teams proactively address gaps and foster a more
          harmonious and productive working environment.
        </p>

        <p className="text-l text-left font-bold mt-10 mb-10">
          Click the markers to see the result when all color energies are present and when a
          particular color energy is missing.
        </p>
        <div className="w-[250px] sm:w-[500px] md:w-[635px] mx-auto relative">
          <img className="w-full" src={chapterImg7} alt="" />
          <div className="absolute top-[10%] right-[10%]">
            <ToggleCard
              position="right"
              isOpen={openCardPosition === 'right'}
              onToggle={handleToggleCard}
              cardContent={
                <div>
                  <p className="text-md sm:text-xl font-bold mb-0 sm:mb-5">
                    SUSTAINABLE, EXTRAORDINARY RESULTS
                  </p>
                </div>
              }
            />
          </div>
          <div className="absolute top-[30%] right-[10%]">
            <ToggleCard
              position="commonPosition_1"
              isOpen={openCardPosition === 'commonPosition_1'}
              onToggle={handleToggleCard}
              cardContent={
                <div>
                  <p className="text-md sm:text-xl font-bold mb-0 sm:mb-5">
                    ERRATIC RESULTS, FALSE STARTS
                  </p>
                  <br />
                  <p className="text-l font-light mb-5">
                    A team missing the analytical and detail-oriented approach of Cool Blue may face
                    challenges in maintaining accuracy and thoroughness in their work, risking
                    errors and inefficiencies.
                  </p>
                </div>
              }
            />
          </div>
          <div className="absolute top-[50%] right-[10%]">
            <ToggleCard
              position="commonPosition_2"
              isOpen={openCardPosition === 'commonPosition_2'}
              onToggle={handleToggleCard}
              cardContent={
                <div>
                  <p className="text-md sm:text-xl font-bold mb-0 sm:mb-5">
                    GOOD FIRST RESULTS, EVENTUAL BURNOUT
                  </p>
                  <br />
                  <p className="text-l font-light mb-5">
                    The absence of Earth Green's supportiveness and patience can create an
                    environment where empathy and collaboration are compromised, leading to
                    increased conflict and decreased trust among team members.
                  </p>
                </div>
              }
            />
          </div>
          <div className="absolute top-[70%] right-[10%]">
            <ToggleCard
              position="commonPosition_3"
              isOpen={openCardPosition === 'commonPosition_3'}
              onToggle={handleToggleCard}
              cardContent={
                <div>
                  <p className="text-md sm:text-xl font-bold mb-0 sm:mb-5">RESULTS PLATEAU</p>
                  <br />
                  <p className="text-l font-light mb-5">
                    Without the enthusiasm and sociability of Sunshine Yellow, the team might
                    experience a lack of creativity and motivation, resulting in low morale and
                    reduced innovation.
                  </p>
                </div>
              }
            />
          </div>
          <div className="absolute top-[90%] right-[10%]">
            <ToggleCard
              position="commonPosition_4"
              isOpen={openCardPosition === 'commonPosition_4'}
              onToggle={handleToggleCard}
              cardContent={
                <div>
                  <p className="text-md sm:text-xl font-bold mb-0 sm:mb-5">POOR RESULTS</p>
                  <p className="text-l font-light mb-5">
                    A team lacking the assertiveness and decisiveness of Fiery Red may struggle with
                    making swift decisions and taking bold actions, leading to delays, poor results
                    and missed opportunities.
                  </p>
                </div>
              }
            />
          </div>
        </div>

        <p className="text-2xl text-left font-bold mt-7 mb-7">
          Scenario Example: The Impact of Missing Color Energies in a School Project Team
        </p>
        <p className="text-xl text-left font-light mt-7 mb-7">
          Imagine you're part of a team working on a big school project. Your team uses Insights
          Discovery to understand everyone's strengths and how they contribute to the group. Each
          team member represents one of the four color energies: Fiery Red, Sunshine Yellow, Earth
          Green, and Cool Blue.
        </p>

        <p className="text-xl text-left font-bold mt-7 mb-7">
          Click each tab to see what happens when one of these color energies is missing:
        </p>
        <TabsBlock tabsData={TabsData} />

        <p className="text-2xl text-center font-light mt-7 mb-7">
          In both scenarios, the absence of a specific color energy creates noticeable challenges
          for the team. By understanding the unique contributions of each color energy, your team
          can work to fill these gaps and ensure a more balanced and effective approach to your
          school projects.
        </p>

        <h3 className="text-2xl text-purple-600 font-bold mt-5 mb-1">
          Team Effectiveness Drivers & Behaviors
        </h3>
        <p className="text-xl text-left font-semibold mt-5 mb-5">
          Team effectiveness relies on understanding and leveraging the unique drivers and behaviors
          of each team member, which is where Insights Discovery comes in. Using the four color
          energies—Fiery Red, Sunshine Yellow, Earth Green, and Cool Blue— enhances the team
          members' understanding of their own and others' communication styles and preferences. By
          recognizing these energies, teams can work more smoothly, ensuring everyone plays to their
          strengths. Below is an image that identifies the behaviors and drivers (what motivates
          them) for each color energy.
        </p>

        <div className="mb-5">
          <img className="w-2/3 m-auto" src={chapterImg8} alt="" />
        </div>

        <p className="text-xl text-left font-light mt-5 mb-5">
          <strong>Example</strong> In a group project, someone with Fiery Red energy might take the
          lead, making sure decisions are made quickly and efficiently. A teammate with Sunshine
          Yellow energy could bring creativity and enthusiasm, keeping the team motivated and
          inspired. Meanwhile, an Earth Green team member ensures everyone feels heard and included,
          fostering a supportive environment. Lastly, someone with Cool Blue energy can handle the
          details and organization, ensuring the project is thorough and well-researched.
        </p>

        <p className="text-2xl text-center font-light mt-20 mb-10">
          Together, these behaviors drive team effectiveness by balancing leadership, creativity,
          support, and precision, making the team strong and successful.
        </p>
        <h3 className="text-2xl text-purple-600 font-bold mt-5 mb-1">
          How Can Color Energies Improve Teamwork?
        </h3>
        <p className="text-2xl text-center font-light mt-20 mb-10">
          Understanding how different color energies can improve teamwork is like unlocking the
          secret to a team of superheroes, each with unique strengths and abilities.
        </p>
        <p className="text-xl text-left font-light mt-5 mb-5">
          Check out these 10 examples of real workplace tasks that depend on collaboration.
        </p>

        <div className="border rounded-3xl mr-8">
          <CarouselComponent data={slidesData} isRequired={false} />
        </div>

        <h3 className="text-2xl mt-2  text-center font-light mb-5">
          Insights Discovery Color Energies are like your secret weapon for better teamwork. By
          understanding yourself and your teammates better, communicating effectively, and embracing
          diversity, you can unleash the full power of your team and accomplish amazing things
          together. So go ahead, collaborate like the superheroes you are, and watch the magic
          happen!
        </h3>
        <button onClick={handleNextChapterClick} className="btn-internal w-full">
          Conclusion
        </button>
      </div>
    </>
  );
};
